import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockIcon from "@mui/icons-material/Lock";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  createPaymentIntent,
  registerForClass,
  checkUserCoi,
} from "../api/apiClient";

const STEP_LABELS = {
  prerequisites: "Prerequisites",
  terms: "Terms & Conditions",
  waiver: "Liability Waiver",
  payment: "Payment & Confirm",
};

/**
 * RegistrationWizard — 3-step dialog for class enrollment.
 *
 * Steps:
 *  0: Prerequisites  — shows class prereqs; student must acknowledge
 *  1: Terms & Conditions — shows generic or class-specific T&Cs; student must accept
 *  2: Payment & Confirm  — shows fee info, collects mock payment, submits registration
 *
 * Props:
 *  open           {boolean}   — controls dialog visibility
 *  onClose        {function}  — called when wizard is cancelled or closed
 *  onComplete     {function}  — called with the Registration object on success
 *  classData      {object}    — the class/course object from the API
 *  tenantConfig   {object}    — tenant config from TenantContext (for genericTermsConditions)
 *  currentUser    {object}    — logged-in user (needs .id)
 *
 * TODO [STRIPE]:
 *  When Stripe is configured, Step 2 should use <PaymentElement /> from @stripe/react-stripe-js
 *  to collect card details. Replace the mock payment block below with:
 *
 *    import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
 *    import { loadStripe } from "@stripe/stripe-js";
 *
 *    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
 *
 *    // Wrap the payment step in:
 *    <Elements stripe={stripePromise} options={{ clientSecret }}>
 *      <PaymentElement />
 *    </Elements>
 *
 *    // Then call stripe.confirmPayment() before calling registerForClass().
 *    // Registration should ideally be confirmed via Stripe webhook on the backend.
 *
 *  Stripe React docs: https://stripe.com/docs/stripe-js/react
 *  Payment Element:  https://stripe.com/docs/payments/payment-element
 */
export default function RegistrationWizard({
  open,
  onClose,
  onComplete,
  classData,
  tenantConfig,
  currentUser,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const [activeStep, setActiveStep] = useState(0);
  const [prerequisitesConfirmed, setPrerequisitesConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [waiverSignature, setWaiverSignature] = useState("");
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [coiBlocked, setCoiBlocked] = useState(false);
  const [coiCheckLoading, setCoiCheckLoading] = useState(false);

  // Auto-check prerequisites when class has none
  const classPrereqs = classData?.prereqs?.trim();
  const hasPrereqs =
    classPrereqs &&
    classPrereqs.toLowerCase() !== "none" &&
    classPrereqs !== "";

  // Liability waiver — only inserted as a step when the tenant requires a
  // digital signature and this class has a physical (hands-on) session.
  const waiverConfig = tenantConfig?.trainingCenter?.liabilityWaiver || {};
  const waiverApplies =
    Boolean(waiverConfig.applyToAllPhysical) &&
    Boolean(waiverConfig.requireDigitalSignature) &&
    classData?.hasPhysicalSession === true;

  const stepKeys = [
    "prerequisites",
    "terms",
    ...(waiverApplies ? ["waiver"] : []),
    "payment",
  ];
  const STEPS = stepKeys.map((key) => STEP_LABELS[key]);
  const currentStepKey = stepKeys[activeStep];
  const paymentStepIndex = stepKeys.indexOf("payment");

  useEffect(() => {
    if (open) {
      // Reset state on each open
      setActiveStep(0);
      setPrerequisitesConfirmed(!hasPrereqs); // auto-confirm when no prereqs
      setTermsAccepted(false);
      setWaiverSignature("");
      setPaymentIntent(null);
      setPaymentError("");
      setSubmitError("");
      setCoiBlocked(false);
      setCoiCheckLoading(false);
    }
  }, [open, hasPrereqs]);

  // Fetch payment intent when reaching Step 2
  const fetchPaymentIntent = useCallback(async () => {
    if (!classData?.id) return;
    setPaymentLoading(true);
    setPaymentError("");
    try {
      const intent = await createPaymentIntent(classData.id);
      setPaymentIntent(intent);
    } catch (err) {
      setPaymentError("Could not load payment information. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  }, [classData?.id]);

  const handleNext = async () => {
    if (activeStep === paymentStepIndex - 1) {
      // Moving to payment step — fetch intent AND check COI
      setCoiCheckLoading(true);
      setCoiBlocked(false);
      const tenantId = tenantConfig?.id || tenantConfig?.tenantId || null;
      try {
        const [, coiResult] = await Promise.all([
          fetchPaymentIntent(),
          tenantId && currentUser?.id
            ? checkUserCoi(tenantId, currentUser.id).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (coiResult && coiResult.satisfied === false) {
          setCoiBlocked(true);
        }
      } finally {
        setCoiCheckLoading(false);
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setSubmitError("");
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const wizardData = {
        prerequisitesConfirmed,
        termsAccepted,
        waiverSignature: waiverApplies ? waiverSignature.trim() : null,
        paymentIntentId: paymentIntent?.paymentIntentId || null,
        tenantId: tenantConfig?.id || tenantConfig?.tenantId || null,
      };
      const registration = await registerForClass(
        classData.id,
        currentUser.id,
        wizardData,
      );
      if (onComplete) onComplete(registration);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Registration failed. Please try again.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Resolved T&C text ────────────────────────────────────────────────────
  const useGenericTerms = classData?.useGenericTerms !== false; // default true
  const termsText = useGenericTerms
    ? tenantConfig?.genericTermsConditions ||
      "By registering, you agree to attend this course and follow all class policies."
    : classData?.termsConditions ||
      "By registering, you agree to the terms set by this course.";

  // ── Payment display ───────────────────────────────────────────────────────
  const paymentRequired = paymentIntent?.required === true;
  const paymentAmount = paymentIntent?.amount ?? 0;
  const currency = (paymentIntent?.currency || "usd").toUpperCase();

  // ── Step content ──────────────────────────────────────────────────────────

  const renderPrerequisitesStep = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: tPrimary, mb: 1.5, fontFamily: "var(--font-display)" }}>
        Prerequisites for <strong>{classData?.title}</strong>
      </Typography>
      {hasPrereqs ? (
        <Box
          sx={{
            bgcolor: "var(--color-surface-2)",
            borderRadius: 2,
            p: 2,
            mb: 2.5,
            border: "1px solid var(--color-hairline-cloud)",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "var(--color-ink-2)", whiteSpace: "pre-wrap" }}
          >
            {classPrereqs}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: "var(--color-success-bg)",
            borderRadius: 2,
            p: 2,
            mb: 2.5,
            border: "1px solid var(--color-success-bg)",
          }}
        >
          <Typography variant="body2" sx={{ color: "#166534" }}>
            No prerequisites are required for this class.
          </Typography>
        </Box>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={prerequisitesConfirmed}
            onChange={(e) => setPrerequisitesConfirmed(e.target.checked)}
            disabled={!hasPrereqs}
            sx={{ color: tPrimary, "&.Mui-checked": { color: tPrimary } }}
          />
        }
        label={
          hasPrereqs
            ? "I confirm that I meet all listed prerequisites."
            : "No prerequisites required — you may proceed."
        }
        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
      />
    </Box>
  );

  const renderTermsStep = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: tPrimary, mb: 1.5, fontFamily: "var(--font-display)" }}>
        Terms & Conditions
      </Typography>
      <Box
        sx={{
          bgcolor: "var(--color-surface-2)",
          borderRadius: 2,
          p: 2,
          mb: 2.5,
          border: "1px solid var(--color-hairline-cloud)",
          maxHeight: 240,
          overflowY: "auto",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "var(--color-ink-2)", whiteSpace: "pre-wrap", lineHeight: 1.7 }}
        >
          {termsText}
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            sx={{ color: tPrimary, "&.Mui-checked": { color: tPrimary } }}
          />
        }
        label="I have read and accept the Terms & Conditions."
        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
      />
    </Box>
  );

  const renderWaiverStep = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: tPrimary, mb: 1.5, fontFamily: "var(--font-display)" }}>
        Liability Waiver
      </Typography>
      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
        This class includes hands-on / physical training. Please read and sign
        the waiver below before registering.
      </Alert>
      <Box
        sx={{
          bgcolor: "var(--color-surface-2)",
          borderRadius: 2,
          p: 2,
          mb: 2.5,
          border: "1px solid var(--color-hairline-cloud)",
          maxHeight: 240,
          overflowY: "auto",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "var(--color-ink-2)", whiteSpace: "pre-wrap", lineHeight: 1.7 }}
        >
          {waiverConfig.text ||
            "By signing below, you acknowledge the physical risks associated with this training and release the training center from liability to the extent permitted by law."}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 1, color: "var(--color-ink-2)" }}>
        Type your full legal name to sign:
      </Typography>
      <input
        type="text"
        value={waiverSignature}
        onChange={(e) => setWaiverSignature(e.target.value)}
        placeholder="Full name"
        style={{
          width: "100%",
          padding: "10px 12px",
          fontSize: "0.9rem",
          borderRadius: 8,
          border: "1px solid #d0d5db",
          boxSizing: "border-box",
        }}
      />
    </Box>
  );

  const renderPaymentStep = () => {
    if (paymentLoading) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={4}
          gap={2}
        >
          <CircularProgress size={32} sx={{ color: tPrimary }} />
          <Typography color="text.secondary">
            Loading payment information…
          </Typography>
        </Box>
      );
    }

    if (paymentError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentError}
        </Alert>
      );
    }

    return (
      <Box>
        {/* COI blocking alert */}
        {coiBlocked && (
          <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            <strong>Registration Blocked — Insurance Required</strong>
            <br />
            This training center requires a valid Certificate of Insurance (COI)
            from your agency before you can register. Please ask your Agency
            Administrator to upload a valid COI, then try again.
          </Alert>
        )}

        {/* Enrollment summary */}
        <Typography variant="subtitle2" sx={{ color: tPrimary, mb: 1.5, fontFamily: "var(--font-display)" }}>
          Enrollment Summary
        </Typography>
        <Box
          sx={{
            bgcolor: "var(--color-surface-2)",
            borderRadius: 2,
            p: 2,
            mb: 2.5,
            border: "1px solid var(--color-hairline-cloud)",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="body2" sx={{ color: "var(--color-ink-2)" }}>
              Course
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {classData?.title}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" sx={{ color: "var(--color-ink-2)" }}>
              Fee
            </Typography>
            {paymentRequired ? (
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoneyIcon sx={{ fontSize: 18, color: tPrimary }} />
                <Typography variant="body2" fontWeight={700} color={tPrimary}>
                  {paymentAmount.toFixed
                    ? paymentAmount.toFixed(2)
                    : paymentAmount}{" "}
                  {currency}
                </Typography>
              </Box>
            ) : (
              <Chip
                label="Free"
                color="success"
                size="small"
                icon={<CheckCircleOutlineIcon />}
              />
            )}
          </Box>
        </Box>

        {/* Payment collection */}
        {paymentRequired ? (
          <Box>
            <Alert severity="info" icon={<LockIcon />} sx={{ mb: 2 }}>
              {/*
               * TODO [STRIPE]: Replace this placeholder block with real Stripe payment collection.
               *
               * 1. Install: npm install @stripe/react-stripe-js @stripe/stripe-js
               * 2. Load Stripe: const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
               * 3. Wrap in <Elements stripe={stripePromise} options={{ clientSecret }}> where
               *    clientSecret comes from paymentIntent.clientSecret returned by createPaymentIntent().
               * 4. Render <PaymentElement /> inside the Elements wrapper.
               * 5. On "Complete Registration" click, call stripe.confirmPayment() BEFORE registerForClass().
               * 6. After payment succeeds, the backend webhook will mark payment as PAID.
               *
               * Stripe React docs: https://stripe.com/docs/stripe-js/react
               * Payment Element:  https://stripe.com/docs/payments/payment-element
               */}
              Payment required: $
              {paymentRequired && paymentAmount?.toFixed
                ? paymentAmount.toFixed(2)
                : paymentAmount}{" "}
              {currency}. Stripe payment integration is pending — your
              registration will be processed and payment collected when Stripe
              is configured.
            </Alert>
            <Typography variant="caption" color="text.secondary">
              Payment Intent (mock): {paymentIntent?.paymentIntentId || "—"}
            </Typography>
          </Box>
        ) : (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
            This class is <strong>free</strong> for you to attend. No payment is
            required.
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </Box>
    );
  };

  // ── Step content map ──────────────────────────────────────────────────────
  const stepRenderers = {
    prerequisites: renderPrerequisitesStep,
    terms: renderTermsStep,
    waiver: renderWaiverStep,
    payment: renderPaymentStep,
  };
  const stepContent = stepKeys.map((key) => stepRenderers[key]());

  // ── Next button disabled logic ────────────────────────────────────────────
  const isNextDisabled =
    (currentStepKey === "prerequisites" && !prerequisitesConfirmed) ||
    (currentStepKey === "terms" && !termsAccepted) ||
    (currentStepKey === "waiver" && !waiverSignature.trim()) ||
    coiCheckLoading;

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      PaperProps={{ sx: { borderRadius: 3, maxWidth: 520, width: "100%" } }}
      fullWidth
    >
      <DialogTitle
        sx={{ pb: 1, pt: 3, px: 3, fontWeight: 700, color: tPrimary, fontFamily: "var(--font-display)" }}
      >
        Register for Class
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  "& .MuiStepLabel-label": {
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Divider sx={{ mx: 3 }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {stepContent[activeStep]}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="text"
          disabled={submitting}
          sx={{ mr: "auto", color: "var(--color-ink-2)" }}
        >
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            variant="outlined"
            disabled={submitting || paymentLoading}
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>
        )}
        {isLastStep ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              submitting || paymentLoading || !!paymentError || coiBlocked
            }
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              bgcolor: tPrimary,
              borderRadius: 2,
              "&:hover": { bgcolor: "var(--color-primary)" },
            }}
          >
            {submitting ? "Registering…" : "Complete Registration"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isNextDisabled}
            sx={{
              bgcolor: tPrimary,
              borderRadius: 2,
              "&:hover": { bgcolor: "var(--color-primary)" },
            }}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
